import { Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";

const STATUS_OPTIONS = ["", "Published", "Published (Cached)", "In Progress", "Failed"];

function formatAbsoluteDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString("fr-FR");
}

function formatRelativeDate(value) {
    if (!value) return "-";
    const now = Date.now();
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts)) return "-";
    const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
    if (diffSec < 60) return "à l'instant";
    const min = Math.floor(diffSec / 60);
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    return `il y a ${d} j`;
}

function statusBadgeClass(status) {
    if (status === "Published") return "bg-emerald-100 text-emerald-700";
    if (status === "Published (Cached)") return "bg-teal-100 text-teal-700";
    if (status === "In Progress") return "bg-blue-100 text-blue-700";
    if (status === "Failed") return "bg-red-100 text-red-700";
    return "bg-slate-100 text-slate-700";
}

function toDateInput(date) {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

function buildCsv(sessions) {
    const headers = [
        "Schedule Name",
        "Device Group",
        "Pin Code",
        "Publish Status",
        "Progress",
        "Date Modified"
    ];
    const lines = [headers.join(",")];
    sessions.forEach((s) => {
        const progress = `${(s.filesSuccess || 0) + (s.filesCached || 0)}/${s.filesTotal || 0}`;
        const row = [
            `"${(s.programmeId || s.sessionId || "").replace(/"/g, '""')}"`,
            `"${(s.clientName || "").replace(/"/g, '""')}"`,
            `"${(s.pinCode || "").replace(/"/g, '""')}"`,
            `"${(s.publishStatus || "").replace(/"/g, '""')}"`,
            `"${progress}"`,
            `"${formatAbsoluteDate(s.dateModified)}"`
        ];
        lines.push(row.join(","));
    });
    return lines.join("\n");
}

function DeploymentHistory() {
    const [sessions, setSessions] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState(toDateInput(new Date(Date.now() - (30 * 24 * 60 * 60 * 1000))));
    const [toDate, setToDate] = useState(toDateInput(new Date()));
    const [expandedRows, setExpandedRows] = useState({});
    const [detailsMap, setDetailsMap] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});

    const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize]);

    const fetchSessions = async (customPage = page) => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/download_history/sessions`, {
                params: {
                    page: customPage,
                    pageSize,
                    from: fromDate,
                    to: toDate,
                    status,
                    search
                }
            });
            setSessions(res.data?.sessions || []);
            setTotal(res.data?.total || 0);
        } catch (error) {
            console.error("Erreur récupération historique sessions:", error);
            setSessions([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let eventSource = null;
        fetchSessions(1);
        setPage(1);

        const streamUrl = `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/download_status/stream?scope=fleet`;
        try {
            eventSource = new EventSource(streamUrl);
            eventSource.addEventListener("download_update", () => {
                fetchSessions(page);
            });
        } catch (error) {
            console.error("Erreur stream history:", error);
        }

        return () => {
            if (eventSource) eventSource.close();
        };
    }, [status, search, fromDate, toDate]);

    const changePage = (newPage) => {
        const p = Math.min(totalPages, Math.max(1, newPage));
        setPage(p);
        fetchSessions(p);
    };

    const toggleRow = async (sessionId, pinCode) => {
        const key = `${sessionId}__${pinCode}`;
        setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
        if (detailsMap[key] || loadingDetails[key]) return;

        setLoadingDetails((prev) => ({ ...prev, [key]: true }));
        try {
            const res = await axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/download_history/session/${encodeURIComponent(sessionId)}`, {
                params: { pinCode }
            });
            setDetailsMap((prev) => ({ ...prev, [key]: res.data || { files: [], aggregate: {} } }));
        } catch (error) {
            console.error("Erreur récupération détail session:", error);
            setDetailsMap((prev) => ({ ...prev, [key]: { files: [], aggregate: {} } }));
        } finally {
            setLoadingDetails((prev) => ({ ...prev, [key]: false }));
        }
    };

    const exportCsv = () => {
        const csv = buildCsv(sessions);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `deployment_history_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const inputCls = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30";

    // Panneau détail (agrégat + liste des fichiers), réutilisé par la table (desktop) ET les cartes (mobile).
    const detailPanel = (key) => {
        const details = detailsMap[key];
        if (loadingDetails[key]) return <p className="text-sm text-slate-500">Chargement détails...</p>;
        return (
            <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 md:grid-cols-5">
                    <div className="rounded border border-slate-200 bg-white p-2">OK: {details?.aggregate?.filesOk || 0}</div>
                    <div className="rounded border border-slate-200 bg-white p-2">Failed: {details?.aggregate?.filesFailed || 0}</div>
                    <div className="rounded border border-slate-200 bg-white p-2">Pending: {details?.aggregate?.filesPending || 0}</div>
                    <div className="rounded border border-slate-200 bg-white p-2">Retrying: {details?.aggregate?.filesRetrying || 0}</div>
                    <div className="rounded border border-slate-200 bg-white p-2">Progress: {Math.round(details?.aggregate?.globalProgress || 0)}%</div>
                </div>
                <div className="max-h-56 overflow-auto rounded border border-slate-200 bg-white">
                    <table className="w-full min-w-max text-left text-xs">
                        <thead className="sticky top-0 bg-slate-100 text-slate-600">
                            <tr>
                                <th className="px-2 py-1">File</th>
                                <th className="px-2 py-1">Status</th>
                                <th className="px-2 py-1">Attempt</th>
                                <th className="px-2 py-1">Reason</th>
                                <th className="px-2 py-1">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(details?.files || []).map((f) => (
                                <tr key={`${key}_${f.fileName}`} className="border-t border-slate-100">
                                    <td className="px-2 py-1 font-medium text-slate-800">{f.fileName}</td>
                                    <td className="px-2 py-1">{f.status}</td>
                                    <td className="px-2 py-1">{f.attempt || 0}</td>
                                    <td className="px-2 py-1 text-red-600">{f.reason || "-"}</td>
                                    <td className="px-2 py-1">{formatAbsoluteDate(f.ts)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6">
            <div>
                <h1 className="text-xl font-semibold text-slate-800">Historique des déploiements</h1>
                <p className="text-sm text-slate-500">Vérifiez a posteriori les publications et téléchargements (date/heure, statut, progression).</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher session, pin, programme…"
                        className={`${inputCls} md:col-span-2`}
                    />
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                        {STATUS_OPTIONS.map((item) => (
                            <option key={item || "all"} value={item}>{item || "Tous statuts"}</option>
                        ))}
                    </select>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputCls} />
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputCls} />
                    <button onClick={exportCsv} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                {/* --- Vue CARTES (mobile) --- */}
                <div className="divide-y divide-slate-100 md:hidden">
                    {loading ? (
                        <div className="px-4 py-6 text-sm text-slate-500">Chargement...</div>
                    ) : sessions.length === 0 ? (
                        <div className="px-4 py-6 text-sm text-slate-500">Aucun déploiement trouvé.</div>
                    ) : sessions.map((row) => {
                        const key = `${row.sessionId}__${row.pinCode}`;
                        const isOpen = !!expandedRows[key];
                        const done = (row.filesSuccess || 0) + (row.filesCached || 0);
                        const total = row.filesTotal || 0;
                        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                        return (
                            <div key={key} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="truncate font-semibold text-slate-800">{row.programmeId || row.sessionId}</div>
                                        <div className="truncate text-xs text-slate-500">{row.clientName || row.pinCode}</div>
                                    </div>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.publishStatus)}`}>
                                        {row.publishStatus}
                                    </span>
                                </div>
                                <div className="mt-3">
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                                        <span className="tabular-nums">{done}/{total} fichiers</span>
                                        <span>{formatRelativeDate(row.dateModified)}</span>
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                    <span className="font-mono text-[11px] text-slate-400">#{row.pinCode}</span>
                                    <button onClick={() => toggleRow(row.sessionId, row.pinCode)}
                                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                                        {isOpen ? "Masquer les détails" : "Voir les détails"}
                                    </button>
                                </div>
                                {isOpen && <div className="mt-3">{detailPanel(key)}</div>}
                            </div>
                        );
                    })}
                </div>

                {/* --- Vue TABLE (desktop) --- */}
                <div className="hidden overflow-auto md:block">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-3 py-3"></th>
                                <th className="px-3 py-3">Schedule Name</th>
                                <th className="px-3 py-3">Device Group</th>
                                <th className="px-3 py-3">Device Use</th>
                                <th className="px-3 py-3">Publish Status</th>
                                <th className="px-3 py-3">Progress</th>
                                <th className="px-3 py-3">Date Modified</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td className="px-3 py-4 text-slate-500" colSpan={7}>Chargement...</td></tr>
                            ) : sessions.length === 0 ? (
                                <tr><td className="px-3 py-4 text-slate-500" colSpan={7}>Aucun déploiement trouvé.</td></tr>
                            ) : sessions.map((row) => {
                                const key = `${row.sessionId}__${row.pinCode}`;
                                const isOpen = !!expandedRows[key];
                                const details = detailsMap[key];
                                const done = (row.filesSuccess || 0) + (row.filesCached || 0);
                                return (
                                    <Fragment key={key}>
                                        <tr key={key} className="border-b border-slate-100 transition-colors hover:bg-indigo-50/30">
                                            <td className="px-3 py-2.5">
                                                <button className="grid h-6 w-6 place-items-center rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50" onClick={() => toggleRow(row.sessionId, row.pinCode)}>
                                                    {isOpen ? "−" : "+"}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2.5 font-medium text-slate-800">{row.programmeId || row.sessionId}</td>
                                            <td className="px-3 py-2.5 text-slate-600">{row.clientName || row.pinCode}</td>
                                            <td className="px-3 py-2.5 text-slate-600">{row.deviceUse || 1}</td>
                                            <td className="px-3 py-2.5">
                                                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.publishStatus)}`}>
                                                    {row.publishStatus}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-slate-600">{done}/{row.filesTotal || 0}</td>
                                            <td className="px-3 py-2.5 text-slate-600">
                                                <div>{formatRelativeDate(row.dateModified)}</div>
                                                <div className="text-xs text-slate-500">({formatAbsoluteDate(row.dateModified)})</div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="bg-slate-50">
                                                <td className="px-3 py-3" colSpan={7}>
                                                    {detailPanel(key)}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-3 text-sm">
                    <span className="font-medium text-slate-600">Total : {total}</span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => changePage(page - 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40" disabled={page <= 1}>Préc.</button>
                        <span className="text-slate-500">{page} / {totalPages}</span>
                        <button onClick={() => changePage(page + 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40" disabled={page >= totalPages}>Suiv.</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DeploymentHistory;
